import { Fragment, useContext } from 'react'
import PropTypes from 'prop-types'
import { ReactQueryDevtools } from 'react-query/devtools'
import { Link, useLocation } from 'react-router-dom'
import { useFlags } from 'launchdarkly-react-client-sdk'

import UpgradePanel from './UpgradePanel'
import { UpgradePanelContext } from './UpgradePanelContext'
import NavLink from './NavLink'

import gremlinMascot from 'assets/gremlin-mascot.svg'
import theme from 'theme'
import useAuth from 'hooks/use-auth'
import SupportAppNavLink from 'support/components/SupportAppNavLink'
import { useManageClusterAccess } from 'hooks/use-manage-cluster-access'
import Image from 'components/common/Image'

export const NavHR = () => (
  <hr
    css={{
      width: '75%',
      marginLeft: 'auto',
      marginRight: 'auto',
      borderTopColor: theme.colors.steel[0],
      marginTop: theme.space[2],
      marginBottom: theme.space[2]
    }}
  />
)

const Nav = ({ userHasTeams }) => {
  const { isAdmin, isFreeEdition } = useAuth()
  const { serviceDiscovery: isServicesEnabled } = useFlags()

  const { pendingRequestsByAssetId } = useManageClusterAccess({
    poll: true,
    skipTeamRequests: true,
    skipRequestsToTeam: false,
    skipAllAssetsRequests: true
  })

  const { pathname: currentPathname } = useLocation()
  const disableForOnboarding =
    currentPathname.startsWith('/onboarding') &&
    currentPathname !== '/onboarding/profile'

  const {
    dispatch,
    state: { upgradePanelOpen }
  } = useContext(UpgradePanelContext)

  return (
    <nav
      aria-label="main-navigation"
      css={{
        background: theme.colors.steel[2],
        zIndex: theme.zIndices[5]
      }}
    >
      <div
        css={{
          position: 'sticky',
          top: 0
        }}
      >
        <Link
          css={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'var(--header-height)',
            paddingLeft: theme.space[1],
            paddingRight: theme.space[1]
          }}
          to="/"
        >
          <Image
            alt="Gremlin"
            css={{
              maxWidth: '75%'
            }}
            src={gremlinMascot}
          />
        </Link>
        {isFreeEdition && (
          <NavLink
            active={
              !upgradePanelOpen &&
              (currentPathname.startsWith('/dashboard') ||
                currentPathname === '/')
            }
            disabled={disableForOnboarding || !userHasTeams}
            icon="grid"
            text="Dashboard"
            to="/dashboard"
          />
        )}
        {isServicesEnabled && (
          <NavLink
            active={
              !upgradePanelOpen && currentPathname.startsWith('/services')
            }
            data-track-action="open-services"
            data-track-category="main-navigation"
            data-track-on="click"
            disabled={disableForOnboarding || !userHasTeams}
            icon="services"
            text="Services"
            to={{
              pathname: '/services',
              state: {
                clearState: true
              }
            }}
          />
        )}
        <NavLink
          enabled
          active={!upgradePanelOpen && currentPathname.startsWith('/scenarios')}
          data-track-action="open-scenarios"
          data-track-category="main-navigation"
          data-track-on="click"
          disabled={disableForOnboarding || !userHasTeams}
          icon="scenarios"
          text="Scenarios"
          to={{
            pathname: '/scenarios/my-scenarios',
            state: {
              clearSearchState: true
            }
          }}
        />
        <NavLink
          active={
            !upgradePanelOpen &&
            (currentPathname.startsWith('/attacks') ||
              currentPathname.startsWith('/alfi'))
          }
          data-track-action="open-attacks"
          data-track-category="main-navigation"
          data-track-on="click"
          disabled={disableForOnboarding || !userHasTeams}
          icon="attacksIcon"
          text="Attacks"
          to="/attacks/infrastructure"
        />
        <NavLink
          active={!upgradePanelOpen && currentPathname.startsWith('/schedules')}
          data-track-action="open-schedules"
          data-track-category="main-navigation"
          data-track-on="click"
          disabled={disableForOnboarding || !userHasTeams}
          icon="schedulesIcon"
          text="Schedules"
          to="/schedules"
        />
        <NavLink
          active={!upgradePanelOpen && currentPathname.startsWith('/clients')}
          data-track-action="open-clients"
          data-track-category="main-navigation"
          data-track-on="click"
          disabled={disableForOnboarding || !userHasTeams}
          icon="clients"
          showNotification={Object.keys(pendingRequestsByAssetId).length > 0}
          text="Clients"
          to="/clients"
        />
        <NavLink
          active={!upgradePanelOpen && currentPathname.startsWith('/reports/')}
          data-track-action="open-reports"
          data-track-category="main-navigation"
          data-track-on="click"
          disabled={disableForOnboarding || !userHasTeams}
          icon="report"
          text="Reports"
          to="/reports/company"
        />
        <NavLink
          active={!upgradePanelOpen && currentPathname === '/admin/reports'}
          data-track-action="open-admin-reports"
          data-track-category="main-navigation"
          data-track-on="click"
          disabled={disableForOnboarding || !userHasTeams}
          enabled={isAdmin}
          icon="report"
          text="Admin Reports"
          to="/admin/reports"
        />
        <NavHR />
        <NavLink
          data-track-action="open-docs"
          data-track-category="main-navigation"
          data-track-on="click"
          href="https://www.gremlin.com/docs/"
          icon="docs"
          text="Docs"
        />
        {!disableForOnboarding && (
          <NavLink
            active={!upgradePanelOpen && currentPathname === '/api'}
            data-track-action="open-api-docs"
            data-track-category="main-navigation"
            data-track-on="click"
            disabled={!userHasTeams}
            icon="api"
            text="API"
            to="/api"
          />
        )}
        {isFreeEdition && (
          <Fragment>
            <NavHR />
            <NavLink
              active={upgradePanelOpen}
              css={{
                pointerEvents: upgradePanelOpen && 'none'
              }}
              data-track-action="open-upgrade-panel"
              data-track-category="main-navigation"
              data-track-on="click"
              disabled={!userHasTeams}
              icon="unlock"
              text="Upgrade"
              onClick={() => {
                dispatch({
                  type: 'toggleUpgradePanel'
                })
              }}
            />
            <UpgradePanel />
          </Fragment>
        )}
        <SupportAppNavLink />
        {process.env.NODE_ENV === 'development' && !window.Cypress && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </div>
    </nav>
  )
}

Nav.propTypes = {
  userHasTeams: PropTypes.bool
}

export default Nav
